import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import {
  commitWorkerImport,
  getWorkerImportTemplate,
  listWorkerImportRows,
  listWorkerImports,
  previewWorkerImportRows,
  uploadWorkerImportFile,
} from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { issueInfo } from "./workers/workerIssues";
import { rowsToCheck, workWordsLine, workWordsRead } from "./workers/importSummary";
import "./punches/karkhana-punches.scss";
import "./daily/karkhana-daily.scss";
import { useWorkerSetup } from "./workers/useWorkerSetup";
import "./workers/karkhana-workers.scss";
import PageHeader from "./ui/PageHeader";
import { saveBase64 } from "./ui/saveBase64";
import { useKT } from "./i18n/kt";
import { kp } from "./karkhanaPaths";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

type Batch = {
  id: string;
  fileName?: string;
  source?: string;
  status: "PREVIEW" | "COMMITTED";
  createdAt?: string;
  uploadedByName?: string;
  counts: Record<string, number>;
  columns?: { index: number; header: string; samples: string[]; field: string | null }[];
};

type Group = { code: string; count: number; examples: string[] };

/** The HKP fields a column of the studio's file can be read as. */
const FIELDS: [string, string][] = [
  ["name", "Name"],
  ["phone", "Mobile"],
  ["work", "Work"],
  ["address", "Village / address"],
  ["code", "Worker code"],
  ["payType", "Pay type"],
  ["dayRate", "Day rate"],
  ["hourRate", "Hour rate"],
  ["notes", "Notes"],
];

/** Count rows per issue and warning, with a few examples each, for the preview summary. */
const groupRows = (rows: any[], key: "issues" | "warnings"): Group[] => {
  const map = new Map<string, Group>();
  for (const row of rows) {
    for (const code of row[key] || []) {
      const g: Group = map.get(code) || { code, count: 0, examples: [] };
      g.count += 1;
      if (g.examples.length < 3) g.examples.push(`${row.rawName || "(no name)"}${row.rawWork ? ` · ${row.rawWork}` : ""}`.trim());
      map.set(code, g);
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
};

const fmtDate = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

/**
 * Add many people at once, the simplest way first: upload the list the studio already has (any
 * columns, codes optional), paste names from WhatsApp, or fill HKP's Excel template. Nothing is
 * added until the preview is confirmed; people already in HKP are never changed.
 */
const KarkhanaWorkerImportPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const role = readRole();
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const { categories, reviewCount, reload } = useWorkerSetup();
  const [batch, setBatch] = useState<Batch | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [past, setPast] = useState<Batch[]>([]);
  const [busy, setBusy] = useState<"" | "upload" | "commit" | "template">("");
  const [over, setOver] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    listWorkerImports().then((res: any) => setPast(res?.success ? res.data?.docs || [] : []));
  }, [role, history]);

  const loadRows = async (id: string) => {
    // Pages of 200 (the server's maximum) until every row is in, so issue counts are exact.
    const all: any[] = [];
    for (let page = 0; page < 25; page++) {
      const res: any = await listWorkerImportRows(id, { offset: page, limit: 200 });
      const docs = res?.success ? res.data?.docs || [] : [];
      all.push(...docs);
      if (docs.length < 200 || all.length >= Number(res?.data?.length || 0)) break;
    }
    setRows(all);
  };

  const reopen = async (b: Batch) => {
    setBatch(b);
    await loadRows(b.id);
  };

  const upload = async (f?: File | null, mapping?: Record<string, number>) => {
    if (!f) return;
    if (!/\.(csv|xlsx|xls)$/i.test(f.name)) {
      notifications({ title: t("Error"), message: t("Choose a CSV or Excel file"), type: "danger" });
      return;
    }
    setBusy("upload");
    try {
      const res: any = await uploadWorkerImportFile(f, mapping);
      if (!res?.success) {
        notifications({ title: t("Error"), message: t(res?.message || "Could not read the file"), type: "danger" });
        return;
      }
      setFile(f);
      setBatch(res.data);
      await loadRows(res.data.id);
    } catch {
      notifications({ title: t("Error"), message: t("Could not upload the file"), type: "danger" });
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  /** One column read as something else: read the same file again with that choice. */
  const remap = (index: number, field: string) => {
    if (!file || !batch?.columns) return;
    const mapping: Record<string, number> = {};
    FIELDS.forEach(([f]) => {
      mapping[f] = -1;
    });
    batch.columns.forEach((c) => {
      if (c.field && c.index !== index) mapping[c.field] = c.index;
    });
    if (field) mapping[field] = index;
    upload(file, mapping);
  };

  const previewPaste = async () => {
    const lines = pasted
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return;
    setBusy("upload");
    try {
      const res: any = await previewWorkerImportRows({ source: "PASTE", fileName: t("Pasted list"), rows: lines.map((line) => ({ line })) });
      if (!res?.success) {
        notifications({ title: t("Error"), message: t(res?.message || "Could not read the list"), type: "danger" });
        return;
      }
      setFile(null);
      setBatch(res.data);
      setPasting(false);
      await loadRows(res.data.id);
    } catch {
      notifications({ title: t("Error"), message: t("Could not reach the server"), type: "danger" });
    } finally {
      setBusy("");
    }
  };

  const template = async () => {
    setBusy("template");
    try {
      const res: any = await getWorkerImportTemplate();
      if (!res?.success) throw new Error(res?.message);
      saveBase64(res.data.fileName || "hkp-workers.xlsx", res.data.base64);
    } catch (e: any) {
      notifications({ title: t("Error"), message: t(e?.message || "Could not make the template"), type: "danger" });
    } finally {
      setBusy("");
    }
  };

  const commit = async () => {
    if (!batch) return;
    setBusy("commit");
    try {
      const res: any = await commitWorkerImport(batch.id);
      if (!res?.success) {
        notifications({ title: t("Error"), message: t(res?.message || "Could not import"), type: "danger" });
        return;
      }
      setBatch(res.data);
      await Promise.all([loadRows(res.data.id), reload()]);
      notifications({ title: t("Imported"), message: t("Added {{n}} people", { n: res.data.createdNow ?? 0 }), type: "success" });
    } catch {
      notifications({ title: t("Error"), message: t("Could not reach the server"), type: "danger" });
    } finally {
      setBusy("");
    }
  };

  if (!canManageKarkhana(role)) return null;

  const counts = batch?.counts || {};
  const ready = counts.READY || 0;
  const review = counts.NEEDS_REVIEW || 0;
  const notes = groupRows(rows.filter((r) => r.status !== "SKIPPED"), "warnings").filter((g) => g.code !== "CODE_WILL_BE_GIVEN");
  const skipped = groupRows(rows.filter((r) => r.status === "SKIPPED"), "issues");
  const given = rows.filter((r) => (r.warnings || []).includes("CODE_WILL_BE_GIVEN") && r.status === "READY").length;
  const checks = rowsToCheck(rows);
  const words = workWordsRead(rows, categories);

  return (
    <div className={`gi-registry-page kw-page km-page${batch?.status === "PREVIEW" ? " has-foot" : ""}`}>
      <PageHeader
        title={
          batch ? (
            <>
              {batch.fileName || t("Pasted list")}
              <small>{t("{{n}} rows read", { n: rows.length })}</small>
            </>
          ) : (
            t("Add many people")
          )
        }
        back={kp.workers()}
      />
      {/* No Workers tabs here (Figma 10b, 10c): earlier rows to check are linked under the choices. */}

      {!batch ? (
        <>
          <div className={`kwi-opt is-main${over ? " is-over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              if (canWrite) upload(e.dataTransfer.files?.[0]);
            }}
          >
            <span className="kwi-n">1</span>
            <div>
              <strong>{t("Upload the list you already have")}</strong>
              <span>{t("Excel or CSV, any columns. HKP finds Name, Mobile, Work and Village (Naam, Kaam, Gaon work too). Codes are optional.")}</span>
              <label className={`btn ResetBtn btn-sm kwi-pick${!canWrite || busy ? " disabled" : ""}`}>
                {busy === "upload" ? t("Checking the file…") : t("Choose file")}
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv" hidden disabled={!canWrite || !!busy} onChange={(e) => upload(e.target.files?.[0])} />
              </label>
            </div>
          </div>
          <div className="kwi-opt">
            <span className="kwi-n">2</span>
            <div>
              <strong>{t("Paste names")}</strong>
              <span>{t("From WhatsApp or notes, one person per line, e.g. “Ganesh weaver 98390 12345”.")}</span>
              {pasting ? (
                <>
                  <textarea
                    className="form-control kwi-paste"
                    rows={6}
                    value={pasted}
                    onChange={(e) => setPasted(e.target.value)}
                    placeholder={"Ganesh Gautam weaver 9839012345\nNandu helper\nAbdul Rangrez dyer"}
                    autoFocus
                  />
                  <div className="kw-toolbar">
                    <Button className="ResetBtn" size="sm" disabled={!canWrite || !!busy || !pasted.trim()} onClick={previewPaste}>
                      {busy === "upload" ? t("Checking…") : t("Check {{n}} lines", { n: pasted.split(/\r?\n/).filter((l) => l.trim()).length })}
                    </Button>
                    <Button outline size="sm" onClick={() => setPasting(false)}>
                      {t("Cancel")}
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
            {/* The whole card opens the paste box. */}
            {!pasting ? <button type="button" className="kwi-cover" disabled={!canWrite} onClick={() => setPasting(true)} aria-label={t("Paste names")} /> : null}
          </div>
          <div className="kwi-opt">
            <span className="kwi-n">3</span>
            <div>
              <strong>{t("Use our Excel template")}</strong>
              <span>{busy === "template" ? t("Making…") : t("English + Hindi headings, dropdown for Work, an example row. Fill and upload.")}</span>
            </div>
            <button type="button" className="kwi-cover" disabled={busy === "template"} onClick={template} aria-label={t("Download template")} />
          </div>
          <div className="kwi-safe">{t("Nothing is added until you check the preview. People already in HKP are never changed.")}</div>
          <p className="kwi-note">{t("Photos can't come from Excel; after the import HKP offers a quick photo round.")}</p>
          {reviewCount ? (
            <Link to={kp.workerReview()} className="kwi-review">
              {t("{{n}} people from earlier imports to check", { n: reviewCount })} ›
            </Link>
          ) : null}

          {past.length ? (
            <div className="kw-card" style={{ marginTop: 16 }}>
              <h3>{t("Earlier imports")}</h3>
              <ul className="kw-issue-list">
                {past.map((b) => (
                  <li
                    key={b.id}
                    className={b.status === "PREVIEW" ? "kw-press" : undefined}
                    role={b.status === "PREVIEW" ? "button" : undefined}
                    tabIndex={b.status === "PREVIEW" ? 0 : undefined}
                    style={b.status === "PREVIEW" ? { cursor: "pointer" } : undefined}
                    onClick={b.status === "PREVIEW" ? () => reopen(b) : undefined}
                    onKeyDown={b.status === "PREVIEW" ? (e) => e.key === "Enter" && reopen(b) : undefined}
                  >
                    <span className={`kw-chip ${b.status === "COMMITTED" ? "is-ok" : ""}`}>
                      {b.status === "COMMITTED" ? t("Imported") : t("Not confirmed · open")}
                    </span>
                    <span>
                      {b.fileName || t("Sheet")} · {fmtDate(b.createdAt)}
                      {b.uploadedByName ? ` · ${b.uploadedByName}` : ""}
                      <span className="kw-muted">
                        {" "}
                        · {t("{{a}} added, {{r}} to check", { a: b.counts?.IMPORTED || 0, r: b.counts?.NEEDS_REVIEW || 0 })}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {batch.status === "PREVIEW" && batch.columns?.length ? (
            <section className="kwi-sec">
              <div className="kwi-sec__head">
                <h3>{t("Your columns")}</h3>
                {file ? <span>{t("tap to change")}</span> : null}
              </div>
              <ul className="kwi-collist">
                {batch.columns.map((c) => (
                  <li key={c.index}>
                    <span>
                      <strong>“{c.header || t("Column {{n}}", { n: c.index + 1 })}”</strong>
                      <small>{c.samples.join(", ")}</small>
                    </span>
                    <select
                      className={`kwi-colpick${c.field ? " is-on" : ""}`}
                      value={c.field || ""}
                      disabled={!file || !!busy}
                      onChange={(e) => remap(c.index, e.target.value)}
                      aria-label={t("Read “{{h}}” as", { h: c.header || c.index + 1 })}
                    >
                      <option value="">{t("Skip")}</option>
                      {FIELDS.map(([f, label]) => (
                        <option key={f} value={f}>
                          {t(label)}
                          {f === c.field ? " ✓" : ""}
                        </option>
                      ))}
                    </select>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <div className="kat-tiles">
            <div>
              <strong>{batch.status === "COMMITTED" ? counts.IMPORTED || 0 : ready}</strong>
              <span>{batch.status === "COMMITTED" ? t("added") : t("ready")}</span>
            </div>
            <div>
              <strong>{review}</strong>
              <span>{t("check")}</span>
            </div>
            <div>
              <strong>{counts.SKIPPED || 0}</strong>
              <span>{t("skipped")}</span>
            </div>
          </div>

          {checks.length || skipped.length ? (
            <div className="kwi-check">
              <strong>{t(checks.length === 1 ? "1 to check" : "{{n}} to check", { n: checks.length })}</strong>
              <ul>
                {checks.map((c) => (
                  <li key={c.row}>
                    {c.text.includes("{{name}}") ? t(c.text, c.vars) : `${c.vars.name}: ${t(c.text, c.vars)}`}
                  </li>
                ))}
                {skipped.map((g) => (
                  <li key={g.code} className="is-skip">
                    {t(g.count === 1 ? "1 row left out: {{why}}" : "{{n}} rows left out: {{why}}", { n: g.count, why: t(issueInfo(g.code).label).toLowerCase() })}
                  </li>
                ))}
              </ul>
              {checks.length ? <span className="kwi-check__note">{t("They wait in To check; nothing is guessed.")}</span> : null}
            </div>
          ) : null}

          {words.length || given ? (
            <p className="kwi-note">
              {words.length ? `${workWordsLine(words)}. ` : ""}
              {given ? t("Codes K_1, J_2… are given automatically.") : ""}
            </p>
          ) : null}

          {batch.status === "PREVIEW" ? (
            <>
              <button
                type="button"
                className="btn btn-link p-0 kwi-again"
                onClick={() => {
                  setBatch(null);
                  setRows([]);
                  setFile(null);
                }}
              >
                {t("Choose another file")}
              </button>
              <div className="kf-foot">
                <button type="button" className="kd-btn kd-btn--primary kwi-add" disabled={!canWrite || busy === "commit" || (!ready && !review)} onClick={commit}>
                  <span>
                    {busy === "commit" ? t("Adding…") : ready ? t(ready === 1 ? "Add 1 person" : "Add {{n}} people", { n: ready }) : t("Send {{r}} to To check", { r: review })}
                  </span>
                  {ready && review ? <small>{t("{{r}} wait in “To check”", { r: review })}</small> : null}
                </button>
              </div>
            </>
          ) : (
            <div className="kw-toolbar">
              <Link className="btn btn-hastkala btn-sm" to={kp.workerPhotos()}>
                📷 {t("Take their photos")}
              </Link>
              {review ? (
                <Link className="btn btn-hastkala-outline btn-sm" to={kp.workerReview()}>
                  {t("Check {{n}} people", { n: review })}
                </Link>
              ) : null}
              <Link className="btn btn-hastkala-outline btn-sm" to={kp.workers()}>
                {t("See all workers")}
              </Link>
            </div>
          )}

          {notes.length ? (
            <div className="kw-card">
              <h3>{t("Added, but worth a look")}</h3>
              <ul className="kw-issue-list">
                {notes.map((g) => (
                  <li key={g.code}>
                    <span className="kw-issue-n">{g.count}</span>
                    <span>
                      <span className="kw-chip is-info">{t(issueInfo(g.code).label)}</span>{" "}
                      <span className="kw-muted">{t(issueInfo(g.code).help)}</span>
                      <br />
                      <span className="kw-code kw-muted">{g.examples.join(" · ")}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

        </>
      )}
    </div>
  );
};

export default KarkhanaWorkerImportPage;
